import { io } from "socket.io-client";
import { toast } from 'react-toastify';

let socket = null;
let isConnecting = false;
let connectionRole = null; // Track which role connected
let connectionId = null; // Track which user ID connected
let socketWarningShown = false; // Track if we've already warned about missing socket to avoid spam

// Store callbacks for listeners that need to be attached when socket connects
const pendingCallbacks = {
    admin: {
        receiveMessage: null
    },
    user: {
        receiveMessage: null
    }
};

// Get socket URL from environment - use VITE_WS_BASE_URL or fallback to API base URL
const getSocketUrl = () => {
    // Try VITE_WS_BASE_URL first (for WebSocket-specific URL)
    let wsBaseUrl = import.meta.env.VITE_WS_BASE_URL;

    // Fallback to VITE_API_BASE_URL if WS_BASE_URL is not set
    if (!wsBaseUrl) {
        wsBaseUrl = import.meta.env.VITE_API_BASE_URL;
    }

    // Final fallback to default development URL
    if (!wsBaseUrl) {
        wsBaseUrl = 'http://feelbetter-dev.it-mitra.com/api';
        console.warn('⚠️ Socket: Using default WebSocket URL. Set VITE_WS_BASE_URL or VITE_API_BASE_URL in .env file.');
    }

    // Remove /api suffix if present and trim any whitespace
    const socketUrl = wsBaseUrl.trim().replace(/\/api\/?$/, '');

    console.log('🔌 Socket URL:', socketUrl);

    return socketUrl;
};

/**
 * Connect socket with role-based authentication
 * @param {Object} params - Connection parameters
 * @param {string} params.role - User role (admin, doctor, patient)
 * @param {string} params.id - User ID
 * @returns {Object} Socket instance
 */
export const connectSupportSocket = ({ role, id }) => {
    // Check if user is authenticated before creating socket
    const userId = localStorage.getItem('ID');
    const userRole = localStorage.getItem('Role');
    const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';

    // Don't create socket if user is not authenticated
    if (!userId || !userRole || !isAuthenticated) {
        console.log('📵 Support socket: User not authenticated, skipping connection');
        return null;
    }

    // Validate that the provided role and id match the authenticated user
    if (role !== userRole || id !== userId) {
        console.warn('⚠️ Support socket: Role/ID mismatch with authenticated user');
        return null;
    }

    // If socket exists and is connected with the same role/id, return it
    if (socket?.connected && connectionRole === role && connectionId === id) {
        console.log('✅ Support socket already connected:', socket.id);
        return socket;
    }

    // If trying to connect with different user, disconnect first
    if (socket && (connectionRole !== role || connectionId !== id)) {
        console.log('🔄 Different user detected, disconnecting old socket');
        socket.disconnect();
        socket = null;
    }

    // If connection is in progress, wait
    if (isConnecting) {
        console.log('⏳ Support socket connection in progress');
        return socket;
    }

    // If socket exists but is disconnected, reconnect it
    if (socket && !socket.connected) {
        console.log('🔄 Support socket exists but disconnected, reconnecting...');
        socket.connect();
        connectionRole = role;
        connectionId = id;
        return socket;
    }

    // Create new socket connection
    console.log('🆕 Creating NEW socket connection for:', { role, id });
    isConnecting = true;
    connectionRole = role;
    connectionId = id;
    socketWarningShown = false; // Reset warning flag when creating new socket

    try {
        const socketUrl = getSocketUrl();

        if (!socketUrl) {
            console.error('❌ Cannot create socket: No valid socket URL available');
            isConnecting = false;
            toast.warning('Support service configuration error. Please contact support.');
            return null;
        }

        console.log('🔌 Connecting support socket to:', socketUrl);

        // Backend expects IDs in query params, not auth
        const queryParams = {};
        if (role === 'admin') {
            queryParams.adminId = id;
        } else if (role === 'doctor') {
            queryParams.doctorId = id;
        } else if (role === 'patient') {
            queryParams.patientId = id;
        }

        // Add FCM token if available (for push notifications)
        const fcmToken = localStorage.getItem('fcmToken');
        if (fcmToken) {
            queryParams.fcmToken = fcmToken;
            console.log('📱 Including FCM token in socket connection');
        } else {
            console.warn('⚠️ No FCM token found - notifications may not work');
        }

        // Socket.IO configuration
        // Uses default /socket.io/ path (matches Apache proxy: /socket.io/ → backend:5000/socket.io/)
        // Apache handles WebSocket upgrade via ProxyPass with upgrade=WebSocket
        console.log('🔨 Creating new Socket.IO instance with config:', {
            url: socketUrl,
            queryParams,
            transports: ["websocket", "polling"]
        });

        socket = io(socketUrl, {
            // No path option = use default /socket.io/ (matches Apache config)
            transports: ["websocket", "polling"],  // WebSocket first, fallback to polling
            upgrade: true,
            autoConnect: true,
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            timeout: 20000,
            query: queryParams  // Backend reads from socket.handshake.query
        });

        console.log('✅ Socket.IO instance created:', socket);
        console.log('🔌 Socket connecting state:', socket.connecting);
        console.log('🔌 Socket connected state:', socket.connected);

        socket.on("connect", () => {
            console.log("✅ Support socket connected:", socket.id);
            console.log(`📡 Connected as ${role} with ID: ${id}`);
            console.log(`🔌 Transport:`, socket.io.engine.transport.name); // Shows 'polling' or 'websocket'
            isConnecting = false;

            // Attach any pending listeners
            if (role === 'admin' && pendingCallbacks.admin.receiveMessage) {
                console.log('🎧 Attaching pending receiveSupportMessage listener for admin');
                socket.off('receiveSupportMessage');
                socket.on('receiveSupportMessage', pendingCallbacks.admin.receiveMessage);
                pendingCallbacks.admin.receiveMessage = null;
            } else if ((role === 'doctor' || role === 'patient') && pendingCallbacks.user.receiveMessage) {
                console.log('🎧 Attaching pending receiveSupportMessage listener for user');
                socket.off('receiveSupportMessage');
                socket.on('receiveSupportMessage', pendingCallbacks.user.receiveMessage);
                pendingCallbacks.user.receiveMessage = null;
            }

            // IMPORTANT: Backend automatically sends tickets on connection
            // For admin: unassignedTickets, adminTickets, closedTickets
            // For user: userTickets
            // Listeners should already be attached by the component, but log to verify
            if (role === 'admin') {
                console.log('🔍 Admin connected - checking if ticket listeners are attached...');
                const hasUnassigned = socket.listeners('unassignedTickets').length > 0;
                const hasAdmin = socket.listeners('adminTickets').length > 0;
                const hasClosed = socket.listeners('closedTickets').length > 0;
                console.log('   unassignedTickets listeners:', hasUnassigned ? '✅' : '❌');
                console.log('   adminTickets listeners:', hasAdmin ? '✅' : '❌');
                console.log('   closedTickets listeners:', hasClosed ? '✅' : '❌');

                if (!hasUnassigned || !hasAdmin || !hasClosed) {
                    console.warn('⚠️ WARNING: Some ticket listeners are NOT attached! Backend will emit data but we will miss it!');
                } else {
                    console.log('✅ All ticket listeners are attached - ready to receive automatic emissions');
                }
            }

            // Backend automatically sends tickets based on query params (adminId, doctorId, patientId)
            // No need to emit 'supportConnection' event
        });

        // Log when transport upgrades to WebSocket
        socket.io.engine.on("upgrade", () => {
            console.log("⬆️ Upgraded to:", socket.io.engine.transport.name);
        });

        socket.on("disconnect", (reason) => {
            console.log("❌ Support socket DISCONNECTED:", reason);
            console.log("❌ Disconnect reason details:", {
                reason,
                timestamp: new Date().toISOString(),
                socketId: socket?.id,
                role: connectionRole,
                userId: connectionId
            });
            isConnecting = false;

            // Show user-friendly message for unexpected disconnections
            if (reason === 'io server disconnect') {
                toast.warning('Support service disconnected. Reconnecting...');
            } else if (reason === 'transport close' || reason === 'transport error') {
                toast.warning('Connection lost. Attempting to reconnect...');
            }
        });

        socket.on("connect_error", (err) => {
            console.error("❌ Support socket connection error:", err);
            console.error("   Error details:", {
                message: err.message,
                type: err.type,
                description: err.description
            });
            isConnecting = false;
            toast.warning('Failed to connect to support service. Please check your connection.');
        });

        socket.on("error", (error) => {
            console.error("❌ Support socket error:", error);
            toast.warning(error.message || 'Support service error occurred');
        });

        socket.on("reconnect", (attemptNumber) => {
            console.log("🔄 Support socket RECONNECTED after", attemptNumber, "attempts");
            console.log("✅ Reconnection successful:", {
                socketId: socket?.id,
                role: connectionRole,
                userId: connectionId,
                timestamp: new Date().toISOString()
            });
            toast.success('Reconnected to support service');

            // Re-emit initialization events after reconnection
            // Backend needs to know who we are again after reconnect
            if (connectionRole === 'admin') {
                console.log('🔄 Re-requesting admin tickets after reconnect');
                socket.emit('requestAdminTickets');
            } else if (connectionRole === 'doctor' || connectionRole === 'patient') {
                console.log('🔄 Re-requesting user tickets after reconnect');
                socket.emit('requestUserTickets');
            }
        });

        socket.on("reconnect_attempt", (attemptNumber) => {
            console.log(`🔄 Reconnection attempt #${attemptNumber}...`);
        });

        socket.on("reconnect_error", (err) => {
            console.error("❌ Reconnection error:", err);
        });

        socket.on("reconnect_failed", () => {
            console.error("❌ Reconnection failed after all attempts");
            toast.warning('Unable to reconnect to support service. Please refresh the page.');
        });

        return socket;
    } catch (error) {
        console.error('Error creating support socket:', error);
        isConnecting = false;
        throw error;
    }
};

/**
 * Get existing socket instance
 * @returns {Object|null} Socket instance or null
 */
export const getSupportSocket = () => {
    if (!socket && !socketWarningShown) {
        // Only warn once to avoid console spam
        socketWarningShown = true;
        console.log('ℹ️ Support socket not initialized yet. It will be created automatically.');
    }
    return socket;
};

/**
 * Disconnect support socket
 */
export const disconnectSupportSocket = () => {
    if (socket) {
        console.log('🔌 Disconnecting support socket:', socket.id);
        console.log('   Removing all listeners and closing connection');
        socket.removeAllListeners();
        socket.disconnect();
        socket = null;
        isConnecting = false;
        connectionRole = null;
        connectionId = null;
        console.log('✅ Socket disconnected and cleaned up');
    } else {
        console.log('⚠️ No socket to disconnect');
    }
};

/**
 * Check if socket is connected
 * @returns {boolean}
 */
export const isSupportSocketConnected = () => {
    return socket?.connected || false;
};

/**
 * Manually reconnect the socket
 * @returns {boolean} - true if reconnection initiated, false if no socket exists
 */
export const reconnectSupportSocket = () => {
    if (socket && !socket.connected) {
        console.log('🔄 Manually reconnecting support socket...');
        console.log('   Previous role:', connectionRole, 'ID:', connectionId);
        socket.connect();
        return true;
    } else if (!socket) {
        console.warn('⚠️ No socket instance to reconnect. Use connectSupportSocket() instead.');
        return false;
    } else {
        console.log('✅ Socket already connected');
        return false;
    }
};

// Admin-specific socket events
export const adminSocketEvents = {
    // Request tickets manually (for when component mounts after socket connected)
    requestTickets: () => {
        if (socket) {
            if (socket.connected) {
                console.log('📤 EMIT: requestAdminTickets (socket connected)');
                socket.emit('requestAdminTickets');
            } else {
                console.warn('⚠️ Cannot request tickets: socket not connected');
                // Wait for connection and then request
                socket.once('connect', () => {
                    console.log('📤 EMIT: requestAdminTickets (after connection)');
                    socket.emit('requestAdminTickets');
                });
            }
        } else {
            console.error('❌ Cannot request tickets: socket not available');
        }
    },

    // Listen for ticket lists
    // CRITICAL: These listeners MUST be attached before socket connects
    // Backend automatically emits these events when socket connects
    onUnassignedTickets: (callback) => {
        if (socket) {
            socket.off('unassignedTickets');
            console.log('🎧 Attaching unassignedTickets listener (socket exists, will catch automatic emission)');
            socket.on('unassignedTickets', (data) => {
                console.log('📥 LISTEN: unassignedTickets received:', data);
                callback(data);
            });
        } else {
            console.warn('⚠️ Cannot attach unassignedTickets listener: socket not available yet');
            // Retry after a short delay if socket is being created
            setTimeout(() => {
                if (socket) {
                    socket.off('unassignedTickets');
                    console.log('🎧 Retrying: Attaching unassignedTickets listener');
                    socket.on('unassignedTickets', (data) => {
                        console.log('📥 LISTEN: unassignedTickets received:', data);
                        callback(data);
                    });
                }
            }, 500);
        }
    },
    onAdminTickets: (callback) => {
        if (socket) {
            socket.off('adminTickets');
            console.log('🎧 Attaching adminTickets listener (socket exists, will catch automatic emission)');
            socket.on('adminTickets', (data) => {
                console.log('📥 LISTEN: adminTickets received:', data);
                callback(data);
            });
        } else {
            console.warn('⚠️ Cannot attach adminTickets listener: socket not available yet');
            // Retry after a short delay if socket is being created
            setTimeout(() => {
                if (socket) {
                    socket.off('adminTickets');
                    console.log('🎧 Retrying: Attaching adminTickets listener');
                    socket.on('adminTickets', (data) => {
                        console.log('📥 LISTEN: adminTickets received:', data);
                        callback(data);
                    });
                }
            }, 500);
        }
    },
    onClosedTickets: (callback) => {
        if (socket) {
            socket.off('closedTickets');
            console.log('🎧 Attaching closedTickets listener (socket exists, will catch automatic emission)');
            socket.on('closedTickets', (data) => {
                console.log('📥 LISTEN: closedTickets received:', data);
                callback(data);
            });
        } else {
            console.warn('⚠️ Cannot attach closedTickets listener: socket not available yet');
            // Retry after a short delay if socket is being created
            setTimeout(() => {
                if (socket) {
                    socket.off('closedTickets');
                    console.log('🎧 Retrying: Attaching closedTickets listener');
                    socket.on('closedTickets', (data) => {
                        console.log('📥 LISTEN: closedTickets received:', data);
                        callback(data);
                    });
                }
            }, 500);
        }
    },
    onAdminTicketListUpdate: (callback) => {
        if (socket) { socket.off('adminTicketListUpdate'); socket.on('adminTicketListUpdate', callback); }
    },
    onNewSupportTicket: (callback) => {
        if (socket) {
            socket.off('newSupportTicket');
            socket.on('newSupportTicket', (data) => {
                console.log('📥 LISTEN: newSupportTicket', data);
                callback(data);
            });
        }
    },

    // Accept ticket
    acceptTicket: (ticketId, adminId, adminName) => {
        if (socket) {
            console.log('📤 EMIT: acceptSupportTicket', { ticketId, adminId, adminName });
            socket.emit('acceptSupportTicket', { ticketId, adminId, adminName });
        }
    },
    onTicketAccepted: (callback) => {
        if (socket) { socket.off('ticketAccepted'); socket.on('ticketAccepted', callback); }
    },
    onTicketAlreadyAssigned: (callback) => {
        if (socket) { socket.off('ticketAlreadyAssigned'); socket.on('ticketAlreadyAssigned', callback); }
    },

    // Join ticket
    joinTicket: (ticketId, userId, userRole) => {
        if (socket) socket.emit('joinSupportTicket', { ticketId, userId, userRole });
    },
    onTicketJoined: (callback) => {
        if (socket) { socket.off('supportTicketJoined'); socket.on('supportTicketJoined', callback); }
    },

    // Send message
    sendMessage: (data) => {
        if (socket) {
            console.log('📤 ADMIN: Emitting sendSupportMessage:', data);
            socket.emit('sendSupportMessage', data);
        } else {
            console.error('❌ ADMIN: Socket not available when trying to send message');
        }
    },
    onReceiveMessage: (callback) => {
        const wrappedCallback = (data) => {
            console.log('📥 ADMIN: Received receiveSupportMessage event:', data);
            callback(data);
        };

        if (socket && socket.connected) {
            socket.off('receiveSupportMessage');
            console.log('🎧 ADMIN: Attaching receiveSupportMessage listener (socket connected)');
            socket.on('receiveSupportMessage', wrappedCallback);
        } else if (socket && socket.connecting) {
            console.log('⏳ ADMIN: Socket connecting, storing callback to attach when ready');
            pendingCallbacks.admin.receiveMessage = wrappedCallback;
            // Also attach when socket connects
            socket.once('connect', () => {
                socket.off('receiveSupportMessage');
                console.log('🎧 ADMIN: Socket connected, attaching receiveSupportMessage listener');
                socket.on('receiveSupportMessage', wrappedCallback);
            });
        } else {
            console.log('⏳ ADMIN: Socket not available, storing callback');
            pendingCallbacks.admin.receiveMessage = wrappedCallback;
        }
    },

    // Close ticket
    closeTicket: (ticketId, closedBy) => {
        if (socket) socket.emit('closeSupportTicket', { ticketId, closedBy });
    },
    onTicketClosed: (callback) => {
        if (socket) { socket.off('supportTicketClosed'); socket.on('supportTicketClosed', callback); }
    },

    // Fetch message history
    fetchHistory: (ticketId, page = 1) => {
        if (socket) socket.emit('fetchSupportHistory', { ticketId, page });
    },
    onHistory: (callback) => {
        if (socket) { socket.off('supportHistory'); socket.on('supportHistory', callback); }
    },

    // Assign ticket (from unassigned)
    assignTicket: (ticketId, assignedToAdminId, assignedByAdminId) => {
        if (socket) {
            console.log('📤 EMIT: assignSupportTicket', { ticketId, assignedToAdminId, assignedByAdminId });
            socket.emit('assignSupportTicket', { ticketId, assignedToAdminId, assignedByAdminId });
        }
    },
    onTicketAssignedSuccess: (callback) => {
        if (socket) { socket.off('ticketAssignedSuccess'); socket.on('ticketAssignedSuccess', callback); }
    },

    // Reassign ticket
    reassignTicket: (ticketId, newAdminId, newAdminName, oldAdminName) => {
        if (socket) {
            console.log('📤 EMIT: reassignSupportTicket', { ticketId, newAdminId, newAdminName, oldAdminName });
            socket.emit('reassignSupportTicket', { ticketId, newAdminId, newAdminName, oldAdminName });
        }
    },
    onTicketReassignmentSuccess: (callback) => {
        if (socket) { socket.off('ticketReassignmentSuccess'); socket.on('ticketReassignmentSuccess', callback); }
    },
    onTicketReassigned: (callback) => {
        if (socket) { socket.off('ticketReassigned'); socket.on('ticketReassigned', callback); }
    },

    // Listen for reconnect events (used by components to re-fetch data)
    onReconnect: (callback) => {
        if (socket) { socket.off('reconnect'); socket.on('reconnect', callback); }
    },

    // Remove listeners
    removeListener: (event) => {
        if (socket) socket.off(event);
    }
};

// User (Doctor/Patient) socket events
export const userSocketEvents = {
    // Request tickets manually (for when component mounts after socket connected)
    requestTickets: () => {
        if (socket) {
            if (socket.connected) {
                console.log('📤 EMIT: requestUserTickets (socket connected)');
                socket.emit('requestUserTickets');
            } else {
                console.warn('⚠️ Cannot request tickets: socket not connected');
                // Wait for connection and then request
                socket.once('connect', () => {
                    console.log('📤 EMIT: requestUserTickets (after connection)');
                    socket.emit('requestUserTickets');
                });
            }
        } else {
            console.error('❌ Cannot request tickets: socket not available');
        }
    },

    // Listen for ticket lists
    onUserTickets: (callback) => {
        if (socket) {
            socket.off('userTickets');
            socket.on('userTickets', (data) => {
                console.log('📥 LISTEN: userTickets', data);
                callback(data);
            });
        }
    },
    onUserTicketListUpdate: (callback) => {
        if (socket) { socket.off('userTicketListUpdate'); socket.on('userTicketListUpdate', callback); }
    },

    // Initialize ticket
    initTicket: (data) => {
        if (socket) {
            console.log('📤 EMIT: initSupportTicket', data);
            socket.emit('initSupportTicket', data);
        }
    },
    onTicketInitialized: (callback) => {
        if (socket) {
            socket.off('supportTicketInitialized');
            socket.on('supportTicketInitialized', (ticket) => {
                console.log('📥 LISTEN: supportTicketInitialized', ticket);
                callback(ticket);
            });
        }
    },

    // Join ticket
    joinTicket: (ticketId, userId, userRole) => {
        if (socket) socket.emit('joinSupportTicket', { ticketId, userId, userRole });
    },
    onTicketJoined: (callback) => {
        if (socket) { socket.off('supportTicketJoined'); socket.on('supportTicketJoined', callback); }
    },

    // Send message
    sendMessage: (data) => {
        if (socket) {
            console.log('📤 USER: Emitting sendSupportMessage:', data);
            socket.emit('sendSupportMessage', data);
        } else {
            console.error('❌ USER: Socket not available when trying to send message');
        }
    },
    onReceiveMessage: (callback) => {
        const wrappedCallback = (data) => {
            console.log('📥 USER: Received receiveSupportMessage event:', data);
            callback(data);
        };

        if (socket && socket.connected) {
            socket.off('receiveSupportMessage');
            console.log('🎧 USER: Attaching receiveSupportMessage listener (socket connected)');
            socket.on('receiveSupportMessage', wrappedCallback);
        } else if (socket && socket.connecting) {
            console.log('⏳ USER: Socket connecting, storing callback to attach when ready');
            pendingCallbacks.user.receiveMessage = wrappedCallback;
            // Also attach when socket connects
            socket.once('connect', () => {
                socket.off('receiveSupportMessage');
                console.log('🎧 USER: Socket connected, attaching receiveSupportMessage listener');
                socket.on('receiveSupportMessage', wrappedCallback);
            });
        } else {
            console.log('⏳ USER: Socket not available, storing callback');
            pendingCallbacks.user.receiveMessage = wrappedCallback;
        }
    },

    // Ticket status changes
    onTicketAssigned: (callback) => {
        if (socket) { socket.off('ticketAssigned'); socket.on('ticketAssigned', callback); }
    },
    onTicketStatusChanged: (callback) => {
        if (socket) { socket.off('ticketStatusChanged'); socket.on('ticketStatusChanged', callback); }
    },

    // Close ticket
    closeTicket: (ticketId, closedBy) => {
        if (socket) {
            console.log('📤 EMIT: closeSupportTicket', { ticketId, closedBy });
            socket.emit('closeSupportTicket', { ticketId, closedBy });
        }
    },
    onTicketClosed: (callback) => {
        if (socket) { socket.off('supportTicketClosed'); socket.on('supportTicketClosed', callback); }
    },

    // Duplicate ticket prevention
    onTicketAlreadyOpen: (callback) => {
        if (socket) {
            socket.off('supportTicketAlreadyOpen');
            socket.on('supportTicketAlreadyOpen', (data) => {
                console.log('📥 LISTEN: supportTicketAlreadyOpen', data);
                callback(data);
            });
        }
    },
    onSupportError: (callback) => {
        if (socket) {
            socket.off('supportError');
            socket.on('supportError', (data) => {
                console.log('📥 LISTEN: supportError', data);
                callback(data);
            });
        }
    },

    // Fetch message history
    fetchHistory: (ticketId, page = 1) => {
        if (socket) socket.emit('fetchSupportHistory', { ticketId, page });
    },
    onHistory: (callback) => {
        if (socket) { socket.off('supportHistory'); socket.on('supportHistory', callback); }
    },

    // Listen for reconnect events (used by components to re-fetch data)
    onReconnect: (callback) => {
        if (socket) { socket.off('reconnect'); socket.on('reconnect', callback); }
    },

    // Remove listeners
    removeListener: (event) => {
        if (socket) socket.off(event);
    }
};

export default {
    connectSupportSocket,
    getSupportSocket,
    disconnectSupportSocket,
    isSupportSocketConnected,
    reconnectSupportSocket,
    adminSocketEvents,
    userSocketEvents
};

